import { z } from 'zod';
import { optionalPoint } from './geo.js';
import { WeeklyHoursSchema } from './hours.js';
export const E164Schema = z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, 'phone must be E.164, e.g. +17145550100');
export const OtpRequestSchema = z.object({ phone: E164Schema });
export const OtpVerifySchema = z.object({
    phone: E164Schema,
    code: z.string().regex(/^\d{4,8}$/),
    deviceId: z.string().min(8).max(128),
    /** Where the sign-in happens: the driver app (default) or a passenger account on the public site. */
    intent: z.enum(['driver', 'passenger']).optional(),
});
/** Header every booking-creating and money-moving POST must carry. */
export const IDEMPOTENCY_HEADER = 'idempotency-key';
export const IdempotencyKeySchema = z.string().min(8).max(128);
/** Names that would collide with routes on the public site. */
export const RESERVED_HANDLES = new Set([
    'admin',
    'api',
    'app',
    'b',
    'book',
    'q',
    'w',
    'www',
    'help',
    'support',
    'privacy',
    'terms',
    'about',
    'login',
    'me',
    'signin',
    'drivers',
    'static',
    'assets',
    'rydres',
]);
/** The driver's page name: rydres.com/<handle>. */
export const HandleSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{2,29}$/, '3-30 letters, numbers or hyphens, starting with a letter or number')
    .refine((h) => !RESERVED_HANDLES.has(h), 'that name is reserved');
export const PAYMENT_MODES = ['card', 'direct', 'cash', 'zelle'];
export const PaymentModeSchema = z.enum(PAYMENT_MODES);
export const TIMEZONES = [
    'America/Los_Angeles',
    'America/Denver',
    'America/Phoenix',
    'America/Chicago',
    'America/New_York',
];
const money = (max) => z.number().int().min(0).max(max);
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** An optional address: blank clears it, otherwise the booking rule (3–300 characters). */
const optionalAddress = z
    .string()
    .trim()
    .max(300)
    .refine((s) => s === '' || s.length >= 3, 'at least 3 characters')
    .transform((s) => s || null)
    .nullable()
    .optional();
export const CancelPolicySchema = z.object({
    /** Cancelling at least this many hours before pickup is free. */
    freeHours: z.number().int().min(0).max(24 * 14),
    /** Percentage of the price kept when cancelled inside the free window. */
    lateFeePct: z.number().int().min(0).max(100),
    /** Percentage kept when the rider does not show. */
    noShowPct: z.number().int().min(0).max(100),
});
export const DEFAULT_CANCEL_POLICY = { freeHours: 24, lateFeePct: 50, noShowPct: 100 };
export const ProfileInputSchema = z.object({
    handle: HandleSchema.optional(),
    displayName: z.string().trim().min(2).max(60).optional(),
    legalName: z.string().trim().min(2).max(120).nullable().optional(),
    tcpNumber: z.string().trim().min(3).max(24).nullable().optional(),
    phonePublic: E164Schema.nullable().optional(),
    email: z.string().trim().email().max(200).nullable().optional(),
    bio: optionalText(600),
    serviceAreaText: optionalText(200),
    timezone: z.enum(TIMEZONES).optional(),
    vehicleYear: z.number().int().min(1990).max(2100).nullable().optional(),
    vehicleMake: optionalText(40),
    vehicleModel: optionalText(40),
    vehicleColor: optionalText(30),
    vehiclePlate: optionalText(12),
    vehiclePlateState: z.string().trim().length(2).toUpperCase().nullable().optional(),
    vehicleSeats: z.number().int().min(1).max(14).nullable().optional(),
    bufferMin: z.number().int().min(0).max(240).optional(),
    minNoticeHours: z.number().int().min(0).max(168).optional(),
    maxAdvanceDays: z.number().int().min(1).max(365).optional(),
    autoConfirmDirect: z.boolean().optional(),
    cancelPolicy: CancelPolicySchema.optional(),
    acceptedModes: z.array(PaymentModeSchema).min(1).optional(),
    zelleHandle: optionalText(80),
    hours: WeeklyHoursSchema.optional(),
});
export const ServiceInputSchema = z.object({
    name: z.string().trim().min(2).max(80),
    description: optionalText(300),
    priceCents: money(500_000),
    durationMin: z.number().int().min(15).max(24 * 60),
    maxPassengers: z.number().int().min(1).max(14),
    sort: z.number().int().min(0).max(1000).optional(),
    active: z.boolean().optional(),
    /** Filled in for the rider when they choose this service ("SNA drop-off"). */
    pickupAddress: optionalAddress,
    dropoffAddress: optionalAddress,
    pickupPoint: optionalPoint,
    dropoffPoint: optionalPoint,
});
const riderFields = {
    riderName: z.string().trim().min(2).max(100),
    riderPhone: E164Schema,
    riderEmail: z.string().trim().email().max(200).nullable().optional(),
    passengers: z.number().int().min(1).max(14).default(1),
    pickupAddress: z.string().trim().min(3).max(300),
    dropoffAddress: z.string().trim().min(3).max(300),
    pickupPoint: optionalPoint,
    dropoffPoint: optionalPoint,
    startsAt: z.string().datetime({ offset: true }),
    notes: optionalText(500),
};
/** A rider books one of the driver's fixed-price services from the public page. */
export const PublicBookingSchema = z.object({
    serviceId: z.string().uuid(),
    paymentMode: z.enum(['card', 'direct']),
    ...riderFields,
});
/** The driver records a trip taken outside the page (cash, Zelle, a phone booking). */
export const LogTripSchema = z.object({
    riderName: z.string().trim().min(1).max(100),
    riderPhone: E164Schema.nullable().optional(),
    passengers: z.number().int().min(1).max(14).default(1),
    pickupAddress: z.string().trim().min(3).max(300),
    dropoffAddress: z.string().trim().min(3).max(300),
    pickupPoint: optionalPoint,
    dropoffPoint: optionalPoint,
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }).optional(),
    priceCents: money(500_000),
    paymentMode: z.enum(['cash', 'zelle', 'direct']),
    /** How the trip was arranged, for the waybill: "phone", "text", "in person", ... */
    arrangedVia: z.string().trim().min(2).max(60),
    arrangedAt: z.string().datetime({ offset: true }).optional(),
    notes: optionalText(500),
});
export const CancelBookingSchema = z.object({
    reason: z.string().trim().max(300).optional(),
    /** Override the policy: how much to give back, in cents. */
    refundCents: z.number().int().min(0).optional(),
});
export const RefundSchema = z.object({ amountCents: z.number().int().min(1) });
/** A rider describes a trip that is not one of the fixed services and asks for a price. */
export const QuoteRequestSchema = z.object({
    ...riderFields,
    /** What the rider wants, in their words: hourly, multiple stops, a wedding, ... Optional: the addresses and time often say it all. */
    description: z.string().trim().max(600).optional().default(''),
});
/** The driver prices a custom request. */
export const QuoteSchema = z.object({
    priceCents: money(500_000),
    durationMin: z.number().int().min(15).max(24 * 60).optional(),
    /** Hours the rider has to accept; default 24, never past 2 hours before pickup. */
    expiresHours: z.number().int().min(1).max(24 * 7).optional(),
    message: optionalText(300),
});
export const AcceptQuoteSchema = z.object({ paymentMode: z.enum(['card', 'direct']) });
export const BlockInputSchema = z
    .object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    reason: optionalText(120),
})
    .refine((b) => new Date(b.endsAt) > new Date(b.startsAt), { message: 'end must be after start', path: ['endsAt'] });
/** What the browser's PushManager.subscribe() returns, as sent to the API. */
export const PushSubscriptionSchema = z.object({
    endpoint: z.string().url().max(2000),
    keys: z.object({ p256dh: z.string().min(10).max(200), auth: z.string().min(5).max(100) }),
});
//# sourceMappingURL=dto.js.map